import { Icon as BuoyIcon } from '@foam/buoy/src/components/Icon/Icon';
import { Tooltip } from '@foam/buoy/src/components/Tooltip/Tooltip';
import { MetricsInfoPopover } from '~/components/CreatorChannelMetrics';
import {
  FEATURE_FLAGS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
} from '~/constants';
import { DEMOGRAPHICS_SOURCES } from '~/constants/demographicsSources';
import { SupportedCharts } from '~/constants/platforms';
import { useFetchCreatorPublic } from '~/hooks/api/media-kits/useFetchCreatorPublic';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { getInsightId } from '~/screens/CreatorAudienceDemographics';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution';
import { getPlatformIcon } from '~/screens/PageBuilder/Sidebar';

import { AllCharts } from './AllCharts';
import { AllMetrics } from './AllMetrics';
import { ChartSkeleton } from './ChartSkeleton';

const MODASH_ESTIMATED_DEMOGRAPHICS_TOOLTIP =
  'Indicative audience data. Best read directionally.';

export type TalentChartProps = {
  platform: SOCIAL_PLATFORMS;
  creatorId: string;
  demographics: SupportedCharts[];
  metrics: string[];
};

export const TalentCharts = ({
  platform,
  creatorId,
  demographics,
  metrics,
}: TalentChartProps) => {
  const creator = useFetchCreatorPublic(creatorId);
  const { isEnabled: isModashAudienceDemographicsEnabled } = useFeature(
    FEATURE_FLAGS.foamModashAudienceDemographics,
  );
  const insightId =
    getInsightId({
      creator: creator.data,
      platform,
    }) || '';

  const insights = useInsights({ insightId, platform });

  if (creator.isLoading || insights?.isLoading) return <ChartSkeleton />;
  if (creator.isError) return <p>Error</p>;

  const demographicsSource =
    platform === SOCIAL_PLATFORMS.youtube
      ? creator.data?.ytChannels?.[0]?.demographicsSource
      : (insights.data as { demographicsSource?: string } | undefined)
          ?.demographicsSource;
  const isEstimatedDemographicsSource =
    demographicsSource === DEMOGRAPHICS_SOURCES.modashEstimated;

  const shouldHideEstimatedDemographics =
    isEstimatedDemographicsSource && !isModashAudienceDemographicsEnabled;
  const shouldShowEstimatedDemographicsInfo =
    isEstimatedDemographicsSource && isModashAudienceDemographicsEnabled;
  const shouldHideEstimatedPerformanceMetrics = isEstimatedDemographicsSource;

  const shouldRenderDemographicsSection =
    (demographics?.length ?? 0) > 0 && !shouldHideEstimatedDemographics;
  const shouldRenderMetricsSection =
    (metrics?.length ?? 0) > 0 && !shouldHideEstimatedPerformanceMetrics;

  if (!shouldRenderDemographicsSection && !shouldRenderMetricsSection) {
    return null;
  }

  const name = SOCIAL_PLATFORMS_NAMES[platform];

  return (
    <section className="flex flex-col gap-12 rounded-12">
      {shouldRenderDemographicsSection ? (
        <div className="flex flex-col gap-4">
          <HeadingSection
            platform={platform}
            title={`${name} Audience Demographics`}
            showEstimatedInfo={shouldShowEstimatedDemographicsInfo}
          />
          <AllCharts
            platform={platform}
            demographics={demographics}
            creatorSlug={creator.data?.slug}
          />
        </div>
      ) : null}
      {shouldRenderMetricsSection ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <HeadingSection
              platform={platform}
              title={`${name} Talent & Channel metrics`}
            />
            <MetricsInfoPopover platform={platform} />
          </div>
          <AllMetrics
            platform={platform}
            selectedMetrics={metrics}
            creatorSlug={creator.data?.slug}
          />
        </div>
      ) : null}
    </section>
  );
};

export const HeadingSection = ({
  platform,
  title,
  showEstimatedInfo = false,
}: {
  platform: SOCIAL_PLATFORMS;
  title: string;
  showEstimatedInfo?: boolean;
}) => {
  const PlatformIcon = getPlatformIcon(platform);
  return (
    <div className="flex flex-col justify-between gap-2 md:flex-row md:gap-0">
      <div className="flex items-center gap-2">
        <PlatformIcon />
        <div className="flex items-center gap-1">
          <h3 className="text-20 font-500 text-[var(--media-kit-text-base)]">
            {title}
          </h3>
          {showEstimatedInfo ? (
            <Tooltip
              content={MODASH_ESTIMATED_DEMOGRAPHICS_TOOLTIP}
              placement="top"
            >
              <span
                className="inline-flex items-center justify-center self-center text-neutral-500"
                aria-label="Indicative audience data info"
              >
                <BuoyIcon name="Info" className="text-14" />
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
};
